// ui/src/components/rep/CompensationHistory.jsx
/**
 * @file CompensationHistory.jsx
 * @module ui/src/components/rep/CompensationHistory
 * @description Period-by-period compensation history for the rep persona.
 *
 * S804 redesign — enterprise-grade table pattern (UI-TABLE-01):
 *   SCROLLABLE  : fixed-height container, overflow-y scroll, sticky header
 *   SEARCHABLE  : debounced 300ms client-side on period label (bounded dataset)
 *   COLLAPSIBLE : one row per period (latest version); chevron expands all versions
 *   CLICKABLE   : full row / View button → right panel slide-in
 *   PAGINATION  : keyset cursor via EP 52, IntersectionObserver auto-load
 *
 * Right panel: fixed-position slide-in from right (620px, same as manager dialog).
 * Calls GET /api/v1/payout-summaries/{payout_summary_id} (EP 12.2) — same endpoint
 * used by ManagerShell MyPayout breakdown. Rep own-position RBAC enforced server-side
 * (S803 fix confirmed). Renders: summary metrics, Governing Plan, Component Breakdown,
 * "How It Was Calculated" proof steps — identical to manager right panel (Image 2).
 *
 * payout_summary_id: returned by EP 52 history endpoint (S804 addition to router).
 * Clicking latest version row opens that version's payout summary detail.
 * Expanding amended periods and clicking a specific version opens that run's detail.
 *
 * Scale: rep history is inherently bounded (one rep, ≤40 periods). Client-side
 * search/sort is correct here — not manager hierarchy which needs server-side at 50k.
 *
 * @locked-rules
 * - C-42: Amended periods preserve and display every returned governing version.
 * - INV-3: planStatus is never used as a temporal signal.
 * - XC-6: Valid financial zero is currency zero; null/undefined is unavailable.
 * - XC-7: Period status explicitly named with data-state.
 *
 * @dependencies
 * - GET /api/v1/payout-summaries/{payout_summary_id} (EP 12.2) for right panel
 * - GET /api/v1/rep/{positionId}/history (EP 52) for table data
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
// S945: unified payout explanation panel replaces local PayoutDetailPanel
import PayoutExplanationPanel from '../shared/PayoutExplanationPanel';

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

function formatCurrency(value, currencyCode) {
  if (value === null || value === undefined) return '—';
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currencyCode || 'USD',
    currencyDisplay: 'narrowSymbol',
    maximumFractionDigits: 0,
  }).format(value);
}

function formatPercent(value) {
  if (value === null || value === undefined) return '—';
  return `${new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(value)}%`;
}

function titleCase(value) {
  if (!value) return 'Unknown';
  return value.charAt(0).toUpperCase() + value.slice(1).replace(/_/g, ' ');
}

// ---------------------------------------------------------------------------
// PeriodRow — one collapsible row per period in the table
// ---------------------------------------------------------------------------

function PeriodRow({ period, isSelected, selectedSummaryId, onSelect }) {
  const versions    = period.versions ?? [];
  const [expanded, setExpanded] = useState(false);
  const hasMultiple = versions.length > 1;
  const latest      = versions[0] ?? null;  // latest = first (router DESC created_at)

  const statusCls = `ch-table__status ch-table__status--${period.periodStatus ?? 'open'}`;

  function handleSelect(version) {
    if (!version?.payoutSummaryId) return;
    onSelect({
      payoutSummaryId: version.payoutSummaryId,
      periodLabel:     period.periodLabel,
      versionLabel:    version.versionLabel,
    });
  }

  const isRowSelected = isSelected && !expanded;

  return (
    <>
      <tr
        className={`ch-table__row${isRowSelected ? ' ch-table__row--selected' : ''}`}
        tabIndex={0}
        aria-selected={isRowSelected}
        onClick={() => handleSelect(latest)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleSelect(latest); }
        }}
      >
        <td className="ch-table__cell ch-table__cell--expand">
          {hasMultiple ? (
            <button
              type="button"
              className="icon-button ch-table__chevron"
              aria-label={`${expanded ? 'Collapse' : 'Expand'} versions for ${period.periodLabel}`}
              aria-expanded={expanded}
              onClick={(e) => { e.stopPropagation(); setExpanded((v) => !v); }}
            >
              {expanded ? '⌄' : '›'}
            </button>
          ) : (
            <span className="ch-table__chevron-placeholder" aria-hidden="true" />
          )}
        </td>

        <td className="ch-table__cell">
          <span className="ch-table__period-label">{period.periodLabel}</span>
          {hasMultiple ? (
            <span className="ch-table__amended-badge">{versions.length} versions</span>
          ) : null}
        </td>

        <td className="ch-table__cell">
          <span className={statusCls} data-state={period.periodStatus}>
            {titleCase(period.periodStatus)}
          </span>
        </td>

        <td className="ch-table__cell ch-table__cell--num">
          {latest ? formatCurrency(latest.quota, latest.currencyCode) : '—'}
        </td>
        <td className="ch-table__cell ch-table__cell--num">
          {latest ? formatPercent(latest.attainmentPct) : '—'}
        </td>
        <td className="ch-table__cell ch-table__cell--num">
          {latest ? formatCurrency(latest.calculatedPayout, latest.currencyCode) : '—'}
        </td>
        <td className="ch-table__cell ch-table__cell--num">
          {latest ? formatCurrency(latest.finalPaid, latest.currencyCode) : '—'}
        </td>

        <td className="ch-table__cell ch-table__cell--action">
          {latest?.payoutSummaryId ? (
            <button
              type="button"
              className="text-button ch-table__view-btn"
              tabIndex={-1}
              aria-label={`View payout detail for ${period.periodLabel}`}
              onClick={(e) => { e.stopPropagation(); handleSelect(latest); }}
            >
              View
            </button>
          ) : null}
        </td>
      </tr>

      {expanded && hasMultiple ? versions.map((version, idx) => {
        const vSelected = selectedSummaryId === version.payoutSummaryId;
        return (
          <tr
            key={version.payoutSummaryId ?? version.calculationRunId ?? idx}
            className={`ch-table__row ch-table__row--version${vSelected ? ' ch-table__row--selected' : ''}`}
            tabIndex={0}
            aria-selected={vSelected}
            onClick={() => handleSelect(version)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleSelect(version); }
            }}
          >
            <td className="ch-table__cell ch-table__cell--expand" />
            <td className="ch-table__cell ch-table__cell--version-indent">
              <span className="ch-table__version-dot" aria-hidden="true">○</span>
              <span className="ch-table__version-label">{version.versionLabel}</span>
              <span className="ch-table__governs">
                {idx === 0 ? 'Earlier version'
                  : idx === versions.length - 1 ? 'Latest version'
                  : 'Governing version'}
              </span>
            </td>
            <td className="ch-table__cell" />
            <td className="ch-table__cell ch-table__cell--num">
              {formatCurrency(version.quota, version.currencyCode)}
            </td>
            <td className="ch-table__cell ch-table__cell--num">
              {formatPercent(version.attainmentPct)}
            </td>
            <td className="ch-table__cell ch-table__cell--num">
              {formatCurrency(version.calculatedPayout, version.currencyCode)}
            </td>
            <td className="ch-table__cell ch-table__cell--num">
              {formatCurrency(version.finalPaid, version.currencyCode)}
            </td>
            <td className="ch-table__cell ch-table__cell--action">
              {version.payoutSummaryId ? (
                <button
                  type="button"
                  className="text-button ch-table__view-btn"
                  tabIndex={-1}
                  aria-label={`View ${period.periodLabel} ${version.versionLabel} detail`}
                  onClick={(e) => { e.stopPropagation(); handleSelect(version); }}
                >
                  View
                </button>
              ) : null}
            </td>
          </tr>
        );
      }) : null}
    </>
  );
}

PeriodRow.propTypes = {
  period:          PropTypes.object.isRequired,
  isSelected:      PropTypes.bool.isRequired,
  selectedSummaryId: PropTypes.string,
  onSelect:        PropTypes.func.isRequired,
};

PeriodRow.defaultProps = { selectedSummaryId: null };

// ---------------------------------------------------------------------------
// CompensationHistory — main component
// ---------------------------------------------------------------------------

function CompensationHistory({ positionId }) {
  const [pages,       setPages]       = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error,       setError]       = useState(null);
  const [nextCursor,  setNextCursor]  = useState(null);
  const [hasMore,     setHasMore]     = useState(false);
  const [retryKey,    setRetryKey]    = useState(0);
  const [query,       setQuery]       = useState('');
  const [debouncedQ,  setDebouncedQ]  = useState('');

  // Right panel selection: { payoutSummaryId, periodLabel, versionLabel }
  const [selection, setSelection] = useState(null);

  const sentinelRef = useRef(null);
  const debounceRef = useRef(null);

  // Debounce search 300ms
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedQ(query), 300);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  // Initial load
  useEffect(() => {
    if (!positionId) return;
    setPages([]); setNextCursor(null); setHasMore(false);
    setLoading(true); setError(null);

    fetch(`/api/v1/rep/${encodeURIComponent(positionId)}/history?page_size=20`, {
      credentials: 'include',
    })
      .then((r) => { if (!r.ok) throw new Error(`Unable to load compensation history (${r.status}).`); return r.json(); })
      .then((data) => {
        setPages([data]);
        setNextCursor(data.pagination?.nextCursor ?? null);
        setHasMore(data.pagination?.hasMore ?? false);
      })
      .catch((e) => setError(e))
      .finally(() => setLoading(false));
  }, [positionId, retryKey]);

  // Load more (keyset pagination)
  const loadMore = useCallback(() => {
    if (!positionId || !nextCursor || loadingMore) return;
    setLoadingMore(true);
    fetch(
      `/api/v1/rep/${encodeURIComponent(positionId)}/history?page_size=20&cursor=${encodeURIComponent(nextCursor)}`,
      { credentials: 'include' }
    )
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((data) => {
        setPages((prev) => [...prev, data]);
        setNextCursor(data.pagination?.nextCursor ?? null);
        setHasMore(data.pagination?.hasMore ?? false);
      })
      .catch(() => {})
      .finally(() => setLoadingMore(false));
  }, [positionId, nextCursor, loadingMore]);

  // IntersectionObserver sentinel
  useEffect(() => {
    if (!sentinelRef.current || !hasMore) return;
    const obs = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) loadMore(); },
      { threshold: 0.1 }
    );
    obs.observe(sentinelRef.current);
    return () => obs.disconnect();
  }, [hasMore, loadMore]);

  // Flatten pages → dedupe → sort desc
  const allHistory = useMemo(() => {
    const map = new Map();
    for (const page of pages) {
      for (const period of page.history ?? []) {
        if (!map.has(period.fiscalPeriodId)) {
          map.set(period.fiscalPeriodId, { ...period });
        } else {
          const ex = map.get(period.fiscalPeriodId);
          const existingIds = new Set(ex.versions.map((v) => v.payoutSummaryId));
          for (const v of period.versions) {
            if (!existingIds.has(v.payoutSummaryId)) ex.versions.push(v);
          }
        }
      }
    }
    return [...map.values()].sort((a, b) => b.fiscalPeriodId - a.fiscalPeriodId);
  }, [pages]);

  // Client-side search on period label
  const filtered = useMemo(() => {
    if (!debouncedQ) return allHistory;
    const q = debouncedQ.toLowerCase();
    return allHistory.filter((p) => p.periodLabel?.toLowerCase().includes(q));
  }, [allHistory, debouncedQ]);

  // -------------------------------------------------------------------------
  // Render states
  // -------------------------------------------------------------------------

  if (loading) {
    return (
      <section className="ch" aria-label="Compensation history" aria-busy="true">
        <div className="ch__toolbar">
          <div className="skeleton" style={{ height: 32, width: 220, borderRadius: 8 }} />
        </div>
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="skeleton" style={{ height: 48, marginBottom: 4, borderRadius: 6 }} />
        ))}
        <style>{styles}</style>
      </section>
    );
  }

  if (error) {
    return (
      <section className="ch__error" role="alert">
        <h3>Unable to load compensation history</h3>
        <p>{error.message}</p>
        <button type="button" className="secondary-button" onClick={() => setRetryKey((v) => v + 1)}>
          Retry
        </button>
        <style>{styles}</style>
      </section>
    );
  }

  if (allHistory.length === 0) {
    return (
      <section className="ch__empty" aria-label="Compensation history">
        No compensation history available.
        <style>{styles}</style>
      </section>
    );
  }

  const selectedSummaryId = selection?.payoutSummaryId ?? null;

  return (
    <section className="ch" aria-labelledby="ch-title">
      <header className="ch__header">
        <div>
          <p className="ch__eyebrow">Historical earnings</p>
          <h2 id="ch-title" className="ch__title">Compensation history</h2>
        </div>
        <input
          type="search"
          className="ch__search"
          placeholder="Search periods…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Search compensation periods"
        />
      </header>

      {/* Scrollable table */}
      <div className="ch__table-wrap" role="region" aria-label="Compensation history table">
        <table className="ch-table" role="grid">
          <thead className="ch-table__head">
            <tr>
              <th scope="col" className="ch-table__th ch-table__th--expand" aria-label="Expand" />
              <th scope="col" className="ch-table__th">Period</th>
              <th scope="col" className="ch-table__th">Status</th>
              <th scope="col" className="ch-table__th ch-table__th--num">Quota</th>
              <th scope="col" className="ch-table__th ch-table__th--num">Attainment</th>
              <th scope="col" className="ch-table__th ch-table__th--num">Calculated Payout</th>
              <th scope="col" className="ch-table__th ch-table__th--num">Final Paid</th>
              <th scope="col" className="ch-table__th ch-table__th--action" aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={8} className="ch-table__empty-cell">
                  No periods match "{debouncedQ}".
                </td>
              </tr>
            ) : filtered.map((period) => (
              <PeriodRow
                key={period.fiscalPeriodId}
                period={period}
                isSelected={selection?.fiscalPeriodId === period.fiscalPeriodId ||
                  (selectedSummaryId && period.versions?.some((v) => v.payoutSummaryId === selectedSummaryId))}
                selectedSummaryId={selectedSummaryId}
                onSelect={(sel) => setSelection({ ...sel, fiscalPeriodId: period.fiscalPeriodId })}
              />
            ))}
          </tbody>
        </table>

        {hasMore ? (
          <div ref={sentinelRef} className="ch__sentinel">
            {loadingMore ? <span className="ch__loading-more">Loading more…</span> : null}
          </div>
        ) : null}
      </div>

      {/* Right panel — unified PayoutExplanationPanel (S945) */}
      <PayoutExplanationPanel
        payoutSummaryId={selectedSummaryId}
        periodLabel={selection ? `${selection.periodLabel}${selection.versionLabel ? ` · ${selection.versionLabel}` : ''}` : ''}
        onClose={() => setSelection(null)}
      />

      <style>{styles}</style>
    </section>
  );
}

CompensationHistory.propTypes = {
  positionId: PropTypes.string.isRequired,
};

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const styles = `
  /* ── Shell ────────────────────────────────────────────────────────────── */
  .ch {
    display: flex;
    flex-direction: column;
    gap: 12px;
    color: rgb(var(--color-text));
    position: relative;
  }

  .ch__header {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
  }

  .ch__eyebrow {
    margin: 0 0 2px;
    color: rgb(var(--color-text-muted));
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.05em;
    text-transform: uppercase;
  }

  .ch__title {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
  }

  .ch__toolbar { display: flex; }

  /* ── Search ─────────────────────────────────────────────────────────────── */
  .ch__search {
    height: 34px;
    padding: 0 12px;
    border: 1px solid rgb(var(--color-border));
    border-radius: 8px;
    background: rgb(var(--color-surface));
    color: rgb(var(--color-text));
    font-size: 12px;
    outline: none;
    width: 220px;
  }

  .ch__search:focus {
    border-color: rgb(var(--color-primary) / 0.5);
    box-shadow: 0 0 0 2px rgb(var(--color-primary) / 0.12);
  }

  /* ── Table scroll container ──────────────────────────────────────────────── */
  .ch__table-wrap {
    overflow-y: auto;
    max-height: 520px;
    border: 1px solid rgb(var(--color-border));
    border-radius: 12px;
    background: rgb(var(--color-surface));
  }

  /* ── Table ───────────────────────────────────────────────────────────────── */
  .ch-table {
    width: 100%;
    min-width: 700px;
    border-collapse: collapse;
    font-size: 12px;
  }

  .ch-table__head {
    position: sticky;
    top: 0;
    z-index: 1;
    background: rgb(var(--color-surface-muted));
  }

  .ch-table__th {
    padding: 10px 12px;
    border-bottom: 1px solid rgb(var(--color-border));
    text-align: left;
    color: rgb(var(--color-text-muted));
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    white-space: nowrap;
  }

  .ch-table__th--num    { text-align: right; }
  .ch-table__th--expand { width: 36px; padding: 10px 6px 10px 12px; }
  .ch-table__th--action { width: 64px; }

  /* ── Rows ────────────────────────────────────────────────────────────────── */
  .ch-table__row {
    cursor: pointer;
    transition: background 0.1s;
    outline: none;
  }

  .ch-table__row:hover { background: rgb(var(--color-surface-muted)); }

  .ch-table__row:focus-visible {
    outline: 2px solid rgb(var(--color-focus-ring));
    outline-offset: -2px;
  }

  /* Selected: primary left border + light tint */
  .ch-table__row--selected {
    background: rgb(var(--color-primary) / 0.05);
    box-shadow: inset 3px 0 0 rgb(var(--color-primary));
  }

  .ch-table__row--selected:hover { background: rgb(var(--color-primary) / 0.08); }

  .ch-table__row--version { background: rgb(var(--color-surface-muted)); }
  .ch-table__row--version:hover { background: rgb(var(--color-primary) / 0.04); }

  /* ── Cells ───────────────────────────────────────────────────────────────── */
  .ch-table__cell {
    padding: 11px 12px;
    border-bottom: 1px solid rgb(var(--color-border) / 0.6);
    vertical-align: middle;
  }

  .ch-table__row:last-child .ch-table__cell { border-bottom: 0; }

  .ch-table__cell--expand  { padding: 11px 6px 11px 12px; width: 36px; }
  .ch-table__cell--num     { text-align: right; font-variant-numeric: tabular-nums; }
  .ch-table__cell--action  { text-align: right; }

  .ch-table__cell--version-indent {
    display: flex;
    align-items: center;
    gap: 6px;
    padding-left: 28px;
  }

  .ch-table__period-label { font-weight: 600; }

  .ch-table__amended-badge {
    display: inline-flex;
    margin-left: 6px;
    padding: 1px 7px;
    border: 1px solid rgb(var(--color-warning) / 0.35);
    border-radius: 999px;
    background: rgb(var(--color-warning) / 0.07);
    color: rgb(var(--color-warning));
    font-size: 10px;
    font-weight: 600;
    vertical-align: middle;
  }

  .ch-table__version-dot  { color: rgb(var(--color-primary)); font-size: 10px; flex-shrink: 0; }
  .ch-table__version-label { font-weight: 500; }
  .ch-table__governs { color: rgb(var(--color-text-muted)); font-size: 10px; margin-left: 4px; }

  /* ── Chevron ─────────────────────────────────────────────────────────────── */
  .ch-table__chevron {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    padding: 0;
    border-radius: 4px;
    color: rgb(var(--color-text-muted));
    font-size: 12px;
  }

  .ch-table__chevron:hover { background: rgb(var(--color-surface-muted)); color: rgb(var(--color-text)); }
  .ch-table__chevron-placeholder { display: inline-block; width: 22px; }

  /* ── Status pills ────────────────────────────────────────────────────────── */
  .ch-table__status {
    display: inline-flex;
    padding: 2px 9px;
    border: 1px solid rgb(var(--color-border));
    border-radius: 999px;
    font-size: 10px;
    font-weight: 600;
    white-space: nowrap;
  }

  .ch-table__status--open            { border-color: rgb(var(--color-success)/0.3); background: rgb(var(--color-success)/0.06); color: rgb(var(--color-success)); }
  .ch-table__status--locked          { border-color: rgb(var(--color-warning)/0.3); background: rgb(var(--color-warning)/0.06); color: rgb(var(--color-warning)); }
  .ch-table__status--closed          { background: rgb(var(--color-surface-muted)); color: rgb(var(--color-text-muted)); }


  /* ── View button ─────────────────────────────────────────────────────────── */
  .ch-table__view-btn {
    font-size: 11px;
    padding: 3px 8px;
    border: 1px solid rgb(var(--color-border));
    border-radius: 6px;
    white-space: nowrap;
  }

  .ch-table__view-btn:hover { border-color: rgb(var(--color-primary)/0.4); color: rgb(var(--color-primary)); }

  /* ── Empty / sentinel ────────────────────────────────────────────────────── */
  .ch-table__empty-cell { padding: 32px; text-align: center; color: rgb(var(--color-text-muted)); font-size: 12px; }
  .ch__sentinel { height: 40px; display: flex; align-items: center; justify-content: center; }
  .ch__loading-more { font-size: 11px; color: rgb(var(--color-text-muted)); }

  /* ── Error / empty ───────────────────────────────────────────────────────── */
  .ch__error { padding: 16px 12px; border: 1px solid rgb(var(--color-danger)/0.2); border-radius: 8px; background: rgb(var(--color-danger)/0.05); }
  .ch__error h3 { margin: 0 0 5px; font-size: 13px; }
  .ch__error p  { margin: 0 0 12px; color: rgb(var(--color-text-muted)); font-size: 12px; }
  .ch__empty { padding: 32px 16px; border: 1px dashed rgb(var(--color-border)); border-radius: 8px; background: rgb(var(--color-surface-muted)); color: rgb(var(--color-text-muted)); font-size: 12px; text-align: center; }

  /* ── Right panel backdrop ────────────────────────────────────────────────── */
  .ch-panel-backdrop {
    position: fixed;
    inset: 0;
    z-index: 999;
    background: rgb(0 0 0 / 0.35);
    display: flex;
    justify-content: flex-end;
  }

  /* ── Right panel — 620px matching manager dialog ─────────────────────────── */
  .ch-panel {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    z-index: 1000;
    width: min(620px, 100vw);
    overflow-y: auto;
    border-left: 1px solid rgb(var(--color-border));
    background: rgb(var(--color-bg));
    box-shadow: -8px 0 30px rgb(0 0 0 / 0.14);
    display: flex;
    flex-direction: column;
    animation: ch-slide-in 0.2s ease-out;
  }

  @keyframes ch-slide-in {
    from { transform: translateX(100%); }
    to   { transform: translateX(0); }
  }

  .ch-panel:focus-visible { outline: 2px solid rgb(var(--color-focus-ring)); }

  /* Sticky panel header */
  .ch-panel__header {
    position: sticky;
    top: 0;
    z-index: 2;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    padding: 14px 16px;
    border-bottom: 1px solid rgb(var(--color-border));
    background: rgb(var(--color-surface));
  }

  .ch-panel__eyebrow {
    margin: 0 0 2px;
    color: rgb(var(--color-text-muted));
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.05em;
    text-transform: uppercase;
  }

  .ch-panel__title { margin: 0; font-size: 16px; font-weight: 600; }
  .ch-panel__close { color: rgb(var(--color-text-muted)); font-size: 16px; }
  .ch-panel__close:hover { color: rgb(var(--color-text)); }

  /* Panel body */
  .ch-panel__body { padding: 16px; display: flex; flex-direction: column; gap: 12px; }

  /* Loading / error */
  .ch-panel__loading { display: flex; flex-direction: column; gap: 10px; }
  .ch-panel__skeleton { height: 80px; border-radius: 10px; }
  .ch-panel__error { padding: 12px 14px; border: 1px solid rgb(var(--color-danger)/0.2); border-radius: 8px; background: rgb(var(--color-danger)/0.05); font-size: 12px; color: rgb(var(--color-text-muted)); }
  .ch-panel__error p { margin: 0 0 4px; }

  /* ── Summary metrics — 2-row narrative (mirrors payout-detail layout) ── */
  .ch-panel__metrics-rows {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .ch-panel__metrics {
    display: grid;
    gap: 8px;
  }

  .ch-panel__metrics--3     { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  .ch-panel__metrics--3-end { grid-template-columns: repeat(3, minmax(0, 1fr)); }

  .ch-panel__metric {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 12px 14px;
    border: 1px solid rgb(var(--color-border));
    border-radius: 10px;
    background: rgb(var(--color-surface));
  }

  /* Total Payout — conclusion cell, primary tinted */
  .ch-panel__metric--total {
    border-color: rgb(var(--color-primary) / 0.25);
    background: rgb(var(--color-primary) / 0.05);
  }

  .ch-panel__metric--total strong { color: rgb(var(--color-primary)); }

  /* Empty placeholder — keeps grid alignment when OTE/Rate Band absent */
  .ch-panel__metric--empty {
    border-color: transparent;
    background: transparent;
  }

  .ch-panel__metric span {
    color: rgb(var(--color-text-muted));
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.05em;
    text-transform: uppercase;
  }

  .ch-panel__metric strong {
    font-size: 16px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }

  /* ── Sections ────────────────────────────────────────────────────────────── */
  .ch-panel__section {
    padding: 14px 16px;
    border: 1px solid rgb(var(--color-border));
    border-radius: 12px;
    background: rgb(var(--color-surface));
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .ch-panel__section-eyebrow {
    margin: 0;
    color: rgb(var(--color-text-muted));
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.05em;
    text-transform: uppercase;
  }

  /* ── Governing Plan ──────────────────────────────────────────────────────── */
  .ch-panel__plan-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
  }

  .ch-panel__plan-name { margin: 0; font-size: 14px; font-weight: 600; }
  .ch-panel__plan-code { margin: 2px 0 0; font-size: 11px; color: rgb(var(--color-text-muted)); }
  .ch-panel__plan-governs { margin: 4px 0 0; font-size: 11px; color: rgb(var(--color-success)); }

  .ch-panel__version-pill {
    padding: 2px 9px;
    border: 1px solid rgb(var(--color-primary)/0.25);
    border-radius: 999px;
    background: rgb(var(--color-primary)/0.06);
    color: rgb(var(--color-primary));
    font-size: 11px;
    font-weight: 600;
    white-space: nowrap;
  }

  /* ── Component breakdown ─────────────────────────────────────────────────── */
  .ch-panel__component {
    border: 1px solid rgb(var(--color-border));
    border-radius: 10px;
    overflow: hidden;
  }

  .ch-panel__component-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 14px;
    gap: 8px;
  }

  .ch-panel__component-name-row { display: flex; align-items: center; gap: 8px; }
  .ch-panel__component-name { font-size: 13px; font-weight: 600; }

  .ch-panel__ramp-badge {
    padding: 1px 7px;
    border: 1px solid rgb(var(--color-warning)/0.3);
    border-radius: 999px;
    background: rgb(var(--color-warning)/0.07);
    color: rgb(var(--color-warning));
    font-size: 10px;
    font-weight: 600;
  }

  .ch-panel__component-actions { display: flex; align-items: center; gap: 8px; }
  .ch-panel__component-payout { font-size: 13px; font-weight: 600; font-variant-numeric: tabular-nums; }
  .ch-panel__component-toggle { font-size: 14px; }

  .ch-panel__component-detail {
    padding: 10px 14px;
    border-top: 1px solid rgb(var(--color-border));
    background: rgb(var(--color-surface-muted));
  }

  .ch-panel__component-metrics {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 10px;
  }

  .ch-panel__component-metrics div { display: flex; flex-direction: column; gap: 3px; }
  .ch-panel__component-metrics span { font-size: 10px; color: rgb(var(--color-text-muted)); font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; }
  .ch-panel__component-metrics strong { font-size: 12px; font-weight: 600; font-variant-numeric: tabular-nums; }

  /* ── Proof steps ─────────────────────────────────────────────────────────── */
  .ch-panel__proof { display: flex; flex-direction: column; gap: 2px; }

  .ch-panel__proof-section {
    margin: 8px 0 2px;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: rgb(var(--color-primary));
    opacity: 0.8;
  }

  .ch-panel__proof-row {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
    padding: 6px 10px;
    border-radius: 6px;
    font-size: 12px;
    color: rgb(var(--color-text-muted));
  }

  /* is_result row — primary tint + primary text (S803 rule) */
  .ch-panel__proof-row--result {
    background: rgb(var(--color-primary) / 0.08);
    color: rgb(var(--color-primary));
    font-weight: 600;
  }

  /* is_total row — solid primary bg + white text (S803 rule) */
  .ch-panel__proof-row--total {
    background: rgb(var(--color-primary));
    color: white;
    font-weight: 700;
  }

  /* is_negative row — danger color */
  .ch-panel__proof-row--neg .ch-panel__proof-value { color: rgb(var(--color-danger)); }

  .ch-panel__proof-label { min-width: 0; flex: 1; }
  .ch-panel__proof-note { font-size: 10px; opacity: 0.7; font-weight: 400; }
  .ch-panel__proof-value { white-space: nowrap; font-variant-numeric: tabular-nums; font-weight: 500; }
`;

export default CompensationHistory;
